import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, AlertCircle, Loader2, RefreshCw } from "lucide-react";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import { NoAccessView } from "@/shared/components/NoAccessView";
import { Button } from "@/shared/components/form/Button";
import useTranslations from "@/shared/lang/useTranslations";
import { useAppShellBreadcrumb } from "@/shared/components/layout/useAppShellBreadcrumb";
import { AuditoriaPagination } from "../../auditoria/components/AuditoriaPagination";
import { useQuoterHistoryPage } from "../hooks/useQuoterHistoryPage";
import { QuoterHistoryFilters } from "../components/QuoterHistoryFilters";
import { QuoterHistoryTable } from "../components/QuoterHistoryTable";
import { QuoterHistoryDetailModal } from "../components/QuoterHistoryDetailModal";

export const QuoterHistoryPage = () => {
  const { t } = useTranslations("quoter");
  const { t: tGlobal } = useTranslations("global");
  const breadcrumb = useMemo(
    () => [
      { label: tGlobal("sidebar_section_gestion") },
      { label: tGlobal("sidebar_menu_quoter"), to: "/app/quoter" },
      { label: t("header_history_button") },
    ],
    [t, tGlobal],
  );
  useAppShellBreadcrumb(breadcrumb);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selectedQuoteId, setSelectedQuoteId] = useState<string | null>(null);
  const {
    filters,
    setFilters,
    page,
    pageSize,
    rows,
    totalItems,
    lastPage,
    canGoPrevious,
    canGoNext,
    from,
    to,
    isLoading,
    error,
    paginationOptions,
    pageNumbers,
    handleApplyFilters,
    handleResetFilters,
    handlePageChange,
    handleItemsPerPageChange,
    refetch,
    noAccess,
  } = useQuoterHistoryPage();

  if (user?.role !== "admin") {
    return <NoAccessView />;
  }

  return (
    <div className="space-y-6 p-2">
      <div className="p-8 pb-0 flex justify-between items-center flex-wrap gap-4">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          {t("history_title")}
        </h1>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={refetch}
            disabled={isLoading}
          >
            <RefreshCw
              className={`h-4 w-4 mr-2 ${isLoading ? "animate-spin" : ""}`}
            />
            {t("history_refresh")}
          </Button>
          <Button
            type="button"
            variant="primary"
            className="bg-green-600 hover:bg-green-700 active:bg-green-800 dark:bg-green-500 dark:hover:bg-green-600 dark:active:bg-green-700"
            icon={<ArrowLeft className="size-4" />}
            iconPosition="left"
            onClick={() => navigate("/app/quoter")}
          >
            {t("history_back")}
          </Button>
        </div>
      </div>

      <div className="px-8 pb-8 space-y-6">
        <QuoterHistoryFilters
          filters={filters}
          setFilters={setFilters}
          onApply={handleApplyFilters}
          onReset={handleResetFilters}
          isLoading={isLoading}
          t={t}
        />

        {error && (
          <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
              <AlertCircle className="h-5 w-5 shrink-0" />
              <span>{error}</span>
            </div>
          </div>
        )}

        {noAccess && (
          <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-amber-800 dark:text-amber-200">
            {t("history_no_permission")}
          </div>
        )}

        {!noAccess && (
          <div className="border border-gray-200 dark:border-zinc-800 rounded-lg overflow-hidden bg-white dark:bg-zinc-900">
            {isLoading && rows.length === 0 ? (
              <div className="flex items-center justify-center min-h-[320px]">
                <Loader2 className="size-8 animate-spin text-gray-400 dark:text-zinc-500" />
              </div>
            ) : (
              <>
                <QuoterHistoryTable
                  rows={rows}
                  totalItems={totalItems}
                  itemsPerPage={pageSize}
                  paginationOptions={paginationOptions}
                  onItemsPerPageChange={handleItemsPerPageChange}
                  onViewQuote={(quote) => setSelectedQuoteId(quote.id)}
                  t={t}
                />
                <AuditoriaPagination
                  currentPage={page}
                  lastPage={lastPage}
                  canGoPrevious={canGoPrevious}
                  canGoNext={canGoNext}
                  isLoading={isLoading}
                  pageNumbers={pageNumbers}
                  from={from}
                  to={to}
                  total={totalItems}
                  onPageChange={handlePageChange}
                  recordsText={t("history_pagination_records", { from, to, total: totalItems })}
                  totalPagesText={lastPage != null ? t("history_pagination_total_pages", { lastPage }) : undefined}
                  firstPageTitle={t("history_pagination_first_page")}
                  prevPageTitle={t("history_pagination_prev_page")}
                  nextPageTitle={t("history_pagination_next_page")}
                  lastPageTitle={t("history_pagination_last_page")}
                />
              </>
            )}
          </div>
        )}
      </div>

      <QuoterHistoryDetailModal
        isOpen={selectedQuoteId !== null}
        onClose={() => setSelectedQuoteId(null)}
        quoteId={selectedQuoteId}
      />
    </div>
  );
};
